/**
 * Inicio de DEENEX Activa (`/activa/inicio`, DISENO §1), como tablero (auditoría de diseño del 18/10/2026: con la base
 * subida y una campaña armada, Inicio no mostraba un solo número y la mitad decía «Próximamente»):
 *
 * - el aviso de base vieja, arriba de todo;
 * - tu última campaña, con en qué paso quedó y el botón para seguir (`pasoDeCampana`);
 * - «Para revisar»: lo que falta para que una campaña salga bien (base, WhatsApp, descuentos, tu marca), cada cosa con
 *   su acción. Lo que no se pudo leer no se avisa;
 * - tu público en tres números;
 * - «Así funciona» con el estado real de cada paso. Resultados dice que se mide después del envío.
 *
 * Es del equipo, como todo el menú nuevo hasta que esté completo (fase 0, dudas 1 y 2): la base de ventas no la ve una
 * persona de un cliente.
 */

import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, ArrowRight, CheckCircle2, Layers, Plus } from "lucide-react";
import { activaApi } from "../../campana-nueva/api";
import { num } from "../../campana-nueva/utils/format";
import { usePedido } from "../../emails/hooks/usePedido";
import { ErrorTarjeta } from "../../wa-oficial/components/estadisticas/comun";
import { useCuentaOficialOpcional } from "../../wa-oficial/contexto";
import { cuentaDelModo } from "../../wa-oficial/logic/conexion";
import { baseVentasApi } from "../../base-ventas/api";
import { R_SUBIR_BASE, cuandoSubida } from "../../base-ventas/logic/base";
import type { EstadoBase } from "../../base-ventas/types";
import { brandingApi } from "../../branding/api";
import type { Branding } from "../../branding/types";
import { descuentosApi } from "../../descuentos/api";
import type { Descuentos } from "../../descuentos/types";
import { publicoApi } from "../../publico/api";
import { R_PUBLICO } from "../../publico/logic/publico";
import type { ResumenPublico } from "../../publico/types";
import { segmentacionesApi } from "../../segmentaciones/api";
import { btnPrimario, btnSecundario } from "../../segmentaciones/components/PasosCampana";
import { R_NUEVA_CAMPANA, R_SEGMENTACIONES, nombreDelGrupo, pasoDeCampana, rutaGrupos } from "../../segmentaciones/logic/segmentar";
import type { Segmentacion } from "../../segmentaciones/types";
import { rutaMensajes } from "../../mensajes/logic/mensajes";
import { ResultadoEnCorto } from "../../envios/components/ResultadoEnCorto";
import { Antetitulo, EncabezadoPagina, Pagina } from "../../../components/activa/Pagina";
import { avisosDeInicio, pasoTuBase, saludo, tituloAvisoBase, type Tono } from "../logic/inicio";

const TONO_NUMERO: Record<Tono, string> = { rojo: "bg-red-50 text-red-700", ambar: "bg-amber-50 text-amber-800", neutro: "bg-violet-50 text-violet-700" };
const TONO_ESTADO: Record<Tono, string> = { rojo: "bg-red-50 text-red-700", ambar: "bg-amber-50 text-amber-800", neutro: "bg-slate-100 text-slate-600" };
const TONO_PASO = { violeta: "bg-violet-50 text-violet-700", ambar: "bg-amber-50 text-amber-800", rojo: "bg-red-50 text-red-700", gris: "bg-slate-100 text-slate-600", verde: "bg-emerald-50 text-emerald-700" };
const TARJETA = "rounded-2xl border border-slate-200 bg-white p-5";

function Numero({ n, tono = "neutro" }: { n: string; tono?: Tono }) {
  return (
    <span aria-hidden="true" className={`w-8 h-8 rounded-full flex items-center justify-center font-mono text-[13px] font-medium ${TONO_NUMERO[tono]}`}>
      {n}
    </span>
  );
}

function AvisoBaseVieja({ estado }: { estado: EstadoBase }) {
  return (
    <div role="status" data-testid="aviso-base-vieja" className="flex flex-wrap items-center gap-3.5 rounded-2xl border border-red-200 bg-red-50 px-5 py-4">
      <AlertTriangle size={22} className="flex-shrink-0 text-red-700" aria-hidden="true" />
      <p className="flex-1 basis-[240px] text-sm sm:text-[15px] text-red-900">
        <strong>{tituloAvisoBase(estado)}</strong> Actualizala antes de la próxima campaña, así no le mandás un cupón a quien ya compró.
      </p>
      <Link to={R_SUBIR_BASE} className="min-h-[44px] inline-flex items-center rounded-xl border border-red-200 bg-white px-4 text-sm font-semibold text-red-800 hover:bg-red-100">
        Actualizar base
      </Link>
    </div>
  );
}

/** Un paso de «Así funciona»: con link si lleva a algún lado, y su estado real (o nada, mientras carga). */
function Paso({ n, titulo, texto, estado, tono = "neutro", destino, testId }: { n: string; titulo: string; texto: string; estado?: ReactNode; tono?: Tono; destino?: string; testId: string }) {
  const cuerpo = (
    <>
      <Numero n={n} tono={tono} />
      <span className="text-base font-semibold text-slate-900">{titulo}</span>
      <span className="text-sm text-slate-500">{texto}</span>
      {estado && (
        <span className={`self-start rounded-full px-2.5 py-1 text-xs font-semibold ${TONO_ESTADO[tono]}`} data-testid={`estado-${testId}`}>
          {estado}
        </span>
      )}
    </>
  );
  const clase = "flex-1 flex flex-col gap-1.5 rounded-2xl border border-slate-200 bg-white p-4";
  return (
    <li className="flex">
      {destino ? (
        <Link to={destino} data-testid={`paso-${testId}`} className={`${clase} hover:border-violet-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-400`}>
          {cuerpo}
        </Link>
      ) : (
        <div data-testid={`paso-${testId}`} className={clase}>
          {cuerpo}
        </div>
      )}
    </li>
  );
}

function UltimaCampana({ campana, hayBase }: { campana: Segmentacion | null; hayBase: boolean | null }) {
  if (!campana) {
    return (
      <section aria-labelledby="inicio-ultima" className={`${TARJETA} flex flex-col items-start gap-3`}>
        <Antetitulo>Tu última campaña</Antetitulo>
        <span className="w-11 h-11 rounded-2xl bg-violet-50 text-violet-600 flex items-center justify-center" aria-hidden="true">
          <Layers size={20} />
        </span>
        <h2 id="inicio-ultima" className="text-lg font-semibold text-slate-900">Todavía no hay campañas</h2>
        <p className="text-sm text-slate-600 max-w-md">
          {hayBase === false
            ? "Primero subí tu base de ventas: con ella DEENEX arma los grupos."
            : "DEENEX parte tu base en grupos y el agente le escribe un mensaje a cada uno."}
        </p>
        {hayBase !== false && (
          <Link to={R_NUEVA_CAMPANA} className={btnPrimario}>
            <Plus size={16} aria-hidden="true" />
            Nueva campaña
          </Link>
        )}
      </section>
    );
  }
  const paso = pasoDeCampana(campana);
  const personas = campana.grupos.reduce((t, g) => t + g.personas, 0);
  const ver = campana.estado === "borrador" || campana.estado === "nombres_pendientes" ? rutaGrupos(campana.id) : null;
  return (
    <section aria-labelledby="inicio-ultima" className={`${TARJETA} flex flex-col gap-3`} data-testid="ultima-campana">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0 space-y-1">
          <Antetitulo>Tu última campaña</Antetitulo>
          <h2 id="inicio-ultima" className="text-lg font-semibold text-slate-900">{campana.nombre}</h2>
          <p className="text-sm text-slate-500">
            {cuandoSubida(campana.createdAt)}
            {campana.grupos.length > 0 && ` · ${campana.grupos.length} grupos · ${num(personas)} personas`} · control {campana.pctControl}%
          </p>
        </div>
        <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${TONO_PASO[paso.tono]}`}>{paso.texto}</span>
      </div>
      {campana.resultado && campana.resultado.estado !== "preparada" ? (
        <ResultadoEnCorto resultado={campana.resultado} grupos={campana.grupos} />
      ) : (
        campana.grupos.length > 0 && (
          <span className="flex flex-wrap gap-2 text-sm">
            {campana.grupos.map((g, i) => (
              <span key={g.id} className="rounded-full bg-violet-50 px-2.5 py-1 text-violet-800">
                {nombreDelGrupo(g, i)} · {num(g.personas)}
              </span>
            ))}
          </span>
        )
      )}
      <div className="flex flex-wrap items-center justify-end gap-2 pt-1">
        {ver && (
          <Link to={ver} className={btnSecundario}>
            Ver los grupos
          </Link>
        )}
        <Link to={paso.destino} className={btnPrimario}>
          {paso.accion}
          <ArrowRight size={16} aria-hidden="true" />
        </Link>
      </div>
      <Link to={R_SEGMENTACIONES} className="self-start text-sm font-semibold text-violet-700 hover:text-violet-900">
        Ver todas las campañas
      </Link>
    </section>
  );
}

function ParaRevisar({ avisos, listo }: { avisos: ReturnType<typeof avisosDeInicio>; listo: boolean }) {
  return (
    <section aria-labelledby="inicio-avisos" className={`${TARJETA} flex flex-col gap-3`} data-testid="para-revisar">
      <div className="space-y-1">
        <Antetitulo>Antes de enviar</Antetitulo>
        <h2 id="inicio-avisos" className="text-lg font-semibold text-slate-900">
          Para revisar{avisos.length > 0 && <span className="ml-2 rounded-full bg-violet-50 px-2 py-0.5 text-sm text-violet-700">{avisos.length}</span>}
        </h2>
      </div>
      {avisos.length > 0 ? (
        <ul className="flex flex-col divide-y divide-slate-100">
          {avisos.map((a) => (
            <li key={a.id} className="flex flex-col gap-1.5 py-3 first:pt-0 last:pb-0" data-testid={`aviso-${a.id}`}>
              <p className="text-sm font-semibold text-slate-900">{a.titulo}</p>
              <p className="text-sm text-slate-600">{a.texto}</p>
              <Link to={a.destino} className="self-start inline-flex items-center gap-1 text-sm font-semibold text-violet-700 hover:text-violet-900">
                {a.accion}
                <ArrowRight size={14} aria-hidden="true" />
              </Link>
            </li>
          ))}
        </ul>
      ) : (
        listo && (
          <p className="flex items-center gap-2 text-sm text-emerald-800" data-testid="nada-para-revisar">
            <CheckCircle2 size={18} aria-hidden="true" /> Nada para revisar: tu marca está lista para enviar.
          </p>
        )
      )}
    </section>
  );
}

function TuPublico({ resumen }: { resumen: ResumenPublico }) {
  const t = resumen.totales;
  const numeros = [
    { etiqueta: "Personas", valor: t.personas },
    { etiqueta: "Con WhatsApp", valor: t.conWhatsApp },
    { etiqueta: "Con mail", valor: t.conMail },
  ];
  return (
    <section aria-labelledby="inicio-publico" className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 id="inicio-publico" className="text-xs font-semibold uppercase tracking-wider text-slate-500">
          <span aria-hidden="true">● </span>Tu público
        </h2>
        <Link to={R_PUBLICO} className="text-sm font-semibold text-violet-700 hover:text-violet-900">
          Ver público
        </Link>
      </div>
      <dl className="grid grid-cols-3 gap-3" data-testid="numeros-publico">
        {numeros.map((n) => (
          <div key={n.etiqueta} className="rounded-2xl border border-slate-200 bg-white px-4 py-3">
            <dt className="text-xs text-slate-500">{n.etiqueta}</dt>
            <dd className="text-2xl font-bold text-slate-900 tabular-nums">{num(n.valor)}</dd>
          </div>
        ))}
      </dl>
    </section>
  );
}

/** ¿La marca tiene WhatsApp? La cuenta oficial conectada en este modo o alguna línea por QR activa; null si no se sabe. */
function useWhatsAppConectado(): boolean | null {
  const oficial = useCuentaOficialOpcional();
  const lineas = usePedido<{ estado?: string }[]>(() => activaApi.lines(), "lineas-qr");
  if (oficial?.cuenta && cuentaDelModo(oficial.cuenta, oficial.modo)) return true;
  if (lineas.dato?.some((l) => l.estado === "activa")) return true;
  // sin saber las dos cosas, no se afirma que falta
  if (!oficial || oficial.cargando || !oficial.cuenta || !lineas.dato) return null;
  return false;
}

export default function InicioPage() {
  const base = usePedido<EstadoBase>(() => baseVentasApi.estado(), "base-ventas");
  const estado = base.dato;
  const paso = estado ? pasoTuBase(estado) : null;
  const campanas = usePedido<Segmentacion[]>(() => segmentacionesApi.listar(), "segmentaciones");
  const ultima = campanas.dato?.[0] ?? null;
  const resumen = usePedido<ResumenPublico>(() => publicoApi.resumen(), "resumen");
  const descuentos = usePedido<Descuentos>(() => descuentosApi.ver(), "descuentos");
  const branding = usePedido<Branding>(() => brandingApi.ver(), "branding");
  const whatsapp = useWhatsAppConectado();

  const hayBase = estado ? !!estado.enUso : null;
  const avisos = avisosDeInicio({
    hayBase,
    whatsapp,
    descuentos: descuentos.dato ? descuentos.dato.guardado : null,
    branding: branding.dato ? branding.dato.guardado : null,
  });
  const todoLeido = hayBase !== null && whatsapp !== null && !!descuentos.dato && !!branding.dato;
  const pasoUltima = ultima ? pasoDeCampana(ultima) : null;
  const mensajes =
    ultima && (ultima.estado === "borrador" || ultima.estado === "nombres_pendientes")
      ? { texto: (ultima.mensajesGuardados ?? 0) > 0 ? "Guardados" : "Faltan", destino: ultima.estado === "borrador" ? rutaMensajes(ultima.id) : undefined }
      : null;

  return (
    <Pagina>
      <EncabezadoPagina antetitulo="Inicio" titulo={saludo(activaApi.nombreActual())} bajada="Así está tu marca hoy." />

      {estado?.vieja && <AvisoBaseVieja estado={estado} />}
      {base.error && !estado && <ErrorTarjeta texto={base.error} onReintentar={base.recargar} />}

      <div className="grid gap-5 lg:grid-cols-[3fr_2fr]">
        {campanas.dato ? (
          <UltimaCampana campana={ultima} hayBase={hayBase} />
        ) : campanas.error ? (
          <ErrorTarjeta texto={campanas.error} onReintentar={campanas.recargar} />
        ) : (
          <div aria-hidden="true" className={`${TARJETA} h-48 animate-pulse`} />
        )}
        <ParaRevisar avisos={avisos} listo={todoLeido} />
      </div>

      {resumen.dato && resumen.dato.estado.armadoAt && <TuPublico resumen={resumen.dato} />}

      <section aria-labelledby="inicio-como-funciona" className="space-y-3">
        <h2 id="inicio-como-funciona" className="text-xs font-semibold uppercase tracking-wider text-slate-500">
          <span aria-hidden="true">● </span>Así funciona
        </h2>
        <ol className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <Paso
            n="01"
            titulo="Tu base"
            texto="Tus ventas y clientes"
            testId="tu-base"
            destino={paso?.destino ?? R_PUBLICO}
            tono={paso?.tono}
            estado={
              paso ? (
                <>
                  {paso.texto}
                  {paso.vieja && <span className="sr-only"> (desactualizada)</span>}
                </>
              ) : undefined
            }
          />
          <Paso
            n="02"
            titulo="Segmentar"
            texto="DEENEX arma los grupos"
            testId="segmentar"
            destino={R_NUEVA_CAMPANA}
            estado={ultima ? (ultima.estado === "procesando" ? "Armando los grupos" : ultima.grupos.length ? `${ultima.grupos.length} grupos` : pasoUltima?.texto) : undefined}
          />
          <Paso n="03" titulo="Mensajes" texto="El agente los escribe" testId="mensajes" destino={mensajes?.destino} estado={mensajes?.texto} />
          <Paso
            n="04"
            titulo="Resultados"
            texto="Cuánto vendiste de más"
            testId="resultados"
            destino={pasoUltima?.paso === 4 ? pasoUltima.destino : undefined}
            estado={pasoUltima?.paso === 4 ? pasoUltima.texto : "Después del envío"}
          />
        </ol>
      </section>
    </Pagina>
  );
}
